import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Store, Lock, Mail, LogIn, ArrowLeft, KeyRound } from 'lucide-react';
import './Login.css';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetStep, setResetStep] = useState(1); // 1: request code, 2: enter code & new password
    const { login, isAuthenticated, loading, requestPasswordReset, confirmPasswordReset } = useAuth();
    const navigate = useNavigate();

    // Redirect if already authenticated
    useEffect(() => {
        if (!loading && isAuthenticated) {
            navigate('/', { replace: true });
        }
    }, [isAuthenticated, loading, navigate]);

    // Show loading while checking auth status
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center login-background">
                {/* Animated Background Elements */}
                <div className="login-background-pattern"></div>
                <div className="login-gradient-orb login-gradient-orb-1"></div>
                <div className="login-gradient-orb login-gradient-orb-2"></div>

                <div className="login-card login-loading-card">
                    <div className="login-loading-content">
                        <div className="login-logo-container login-loading-logo">
                            <Store size={36} strokeWidth={2.5} />
                        </div>
                        <div className="login-loading-spinner-container">
                            <div className="login-loading-spinner">
                                <div className="login-loading-spinner-ring"></div>
                                <div className="login-loading-spinner-ring"></div>
                                <div className="login-loading-spinner-ring"></div>
                            </div>
                        </div>
                        <h3 className="login-loading-title">Authenticating</h3>
                        <p className="login-loading-subtitle">Verifying your credentials</p>
                    </div>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsLoading(true);

        const result = await login(email, password);

        if (result.success) {
            // Navigate immediately after successful login
            navigate('/', { replace: true });
        } else {
            setError(result.error || 'Login failed. Please check your credentials.');
            setIsLoading(false);
        }
    };

    const handleForgotPasswordRequest = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsLoading(true);

        const result = await requestPasswordReset(resetEmail);

        if (result.success) {
            setSuccess(`Verification code sent to ${result.destination || 'your email'}`);
            setResetStep(2);
        } else {
            setError(result.error || 'Failed to send verification code. Please try again.');
        }
        setIsLoading(false);
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setIsLoading(true);

        const result = await confirmPasswordReset(resetEmail, verificationCode, newPassword);

        if (result.success) {
            setSuccess('Password reset successfully! You can now sign in with your new password.');
            setTimeout(() => {
                setShowForgotPassword(false);
                setResetStep(1);
                setResetEmail('');
                setVerificationCode('');
                setNewPassword('');
                setConfirmPassword('');
                setSuccess('');
            }, 2000);
        } else {
            setError(result.error || 'Failed to reset password. Please check your verification code.');
        }
        setIsLoading(false);
    };

    const handleBackToLogin = () => {
        setShowForgotPassword(false);
        setResetStep(1);
        setResetEmail('');
        setVerificationCode('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setSuccess('');
    };

    return (
        <div className="min-h-screen flex items-center justify-center login-background">
            {/* Animated Background Elements */}
            <div className="login-background-pattern"></div>
            <div className="login-gradient-orb login-gradient-orb-1"></div>
            <div className="login-gradient-orb login-gradient-orb-2"></div>

            {/* Login Card */}
            <div className="login-card">
                {/* Logo Section */}
                <div className="login-logo-section">
                    <div className="login-logo-container">
                        <Store size={36} strokeWidth={2.5} />
                    </div>
                    <h1 className="login-title">LP Proofer</h1>
                </div>

                {/* Separator */}
                <div className="login-section-separator"></div>

                {!showForgotPassword ? (
                    <>
                        {/* Welcome Message */}
                        <div className="login-welcome">
                            <h2 className="login-welcome-title">Welcome Back</h2>
                            <p className="login-welcome-text">
                                Sign in to access your dashboard
                            </p>
                        </div>

                        {/* Login Form */}
                        <form className="login-form" onSubmit={handleSubmit}>
                            {/* Error Message */}
                            {error && (
                                <div className="login-error">
                                    <div className="login-error-icon">
                                        <svg
                                            className="h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <span className="login-error-text">{error}</span>
                                </div>
                            )}

                            {/* Success Message */}
                            {success && (
                                <div className="login-success">
                                    <div className="login-success-icon">
                                        <svg
                                            className="h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <span className="login-success-text">{success}</span>
                                </div>
                            )}

                            {/* Email Input */}
                            <div className="login-input-group">
                                <label htmlFor="email" className="login-label">
                                    Email Address
                                </label>
                                <div className="login-input-wrapper">
                                    <Mail className="login-input-icon" size={20} />
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        className="login-input"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            {/* Password Input */}
                            <div className="login-input-group">
                                <div className="login-label-row">
                                    <label htmlFor="password" className="login-label">
                                        Password
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotPassword(true)}
                                        className="login-forgot-link"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                                <div className="login-input-wrapper">
                                    <Lock className="login-input-icon" size={20} />
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        className="login-input"
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="login-button"
                            >
                                {isLoading ? (
                                    <>
                                        <svg
                                            className="animate-spin h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        <span>Signing in...</span>
                                    </>
                                ) : (
                                    <>
                                        <LogIn size={20} strokeWidth={2.5} />
                                        <span>Sign In</span>
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Footer */}
                        <div className="login-footer">
                            <div className="login-footer-divider"></div>
                            <p className="login-footer-text">
                                LP Management Portal
                            </p>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Forgot Password Section */}
                        <div className="login-welcome">
                            <button
                                onClick={handleBackToLogin}
                                className="login-back-button"
                            >
                                <ArrowLeft size={20} strokeWidth={2.5} />
                                <span>Back to Sign In</span>
                            </button>
                            <div className="login-forgot-icon-container">
                                <KeyRound size={32} strokeWidth={2.5} />
                            </div>
                            <h2 className="login-welcome-title">
                                {resetStep === 1 ? 'Reset Password' : 'Create New Password'}
                            </h2>
                            <p className="login-welcome-text">
                                {resetStep === 1
                                    ? 'Enter your email to receive a verification code'
                                    : 'Enter the code and your new password'}
                            </p>
                        </div>

                        {/* Reset Password Form */}
                        {resetStep === 1 ? (
                            <form className="login-form" onSubmit={handleForgotPasswordRequest}>
                                {/* Error/Success Messages */}
                                {error && (
                                    <div className="login-error">
                                        <div className="login-error-icon">
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="login-error-text">{error}</span>
                                    </div>
                                )}

                                {success && (
                                    <div className="login-success">
                                        <div className="login-success-icon">
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="login-success-text">{success}</span>
                                    </div>
                                )}

                                <div className="login-input-group">
                                    <label htmlFor="reset-email" className="login-label">
                                        Email Address
                                    </label>
                                    <div className="login-input-wrapper">
                                        <Mail className="login-input-icon" size={20} />
                                        <input
                                            id="reset-email"
                                            name="reset-email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            className="login-input"
                                            placeholder="you@example.com"
                                            value={resetEmail}
                                            onChange={(e) => setResetEmail(e.target.value)}
                                            disabled={isLoading}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="login-button"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Sending Code...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Mail size={20} strokeWidth={2.5} />
                                            <span>Send Verification Code</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        ) : (
                            <form className="login-form" onSubmit={handlePasswordReset}>
                                {/* Error/Success Messages */}
                                {error && (
                                    <div className="login-error">
                                        <div className="login-error-icon">
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="login-error-text">{error}</span>
                                    </div>
                                )}

                                {success && (
                                    <div className="login-success">
                                        <div className="login-success-icon">
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="login-success-text">{success}</span>
                                    </div>
                                )}

                                <div className="login-input-group">
                                    <label htmlFor="verification-code" className="login-label">
                                        Verification Code
                                    </label>
                                    <div className="login-input-wrapper">
                                        <KeyRound className="login-input-icon" size={20} />
                                        <input
                                            id="verification-code"
                                            name="verification-code"
                                            type="text"
                                            required
                                            className="login-input"
                                            placeholder="Enter 6-digit code"
                                            value={verificationCode}
                                            onChange={(e) => setVerificationCode(e.target.value)}
                                            disabled={isLoading}
                                        />
                                    </div>
                                </div>

                                <div className="login-input-group">
                                    <label htmlFor="new-password" className="login-label">
                                        New Password
                                    </label>
                                    <div className="login-input-wrapper">
                                        <Lock className="login-input-icon" size={20} />
                                        <input
                                            id="new-password"
                                            name="new-password"
                                            type="password"
                                            required
                                            className="login-input"
                                            placeholder="Enter new password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            disabled={isLoading}
                                        />
                                    </div>
                                </div>

                                <div className="login-input-group">
                                    <label htmlFor="confirm-password" className="login-label">
                                        Confirm Password
                                    </label>
                                    <div className="login-input-wrapper">
                                        <Lock className="login-input-icon" size={20} />
                                        <input
                                            id="confirm-password"
                                            name="confirm-password"
                                            type="password"
                                            required
                                            className="login-input"
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            disabled={isLoading}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="login-button"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Resetting Password...</span>
                                        </>
                                    ) : (
                                        <>
                                            <KeyRound size={20} strokeWidth={2.5} />
                                            <span>Reset Password</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        )}

                        {/* Footer */}
                        <div className="login-footer">
                            <div className="login-footer-divider"></div>
                            <p className="login-footer-text">
                                LP Management Portal
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Login;
